"use client"

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Subtitles,
  ChevronLeft,
  ChevronRight,
  X,
  Ellipsis,
} from "lucide-react"
// Import the utility functions for detecting media player mode
import { getMediaPlayerMode, getControlsConfig } from "./MediaPlayerModes"
import { useTheme } from "./ThemeManager"
import "./css/EnhancedAudioControls.css"

const EnhancedAudioControls = forwardRef(
  (
    {
      audioSrc,
      videoSrc,
      videoType,
      srtSrc,
      onPlayStateChange,
      onSubtitlesToggle,
      onTimeUpdate,
      onNavigate,
      hasNextLine,
      hasPrevLine,
      initialShowControls = true,
      lessonId,
    },
    ref,
  ) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [showControls, setShowControls] = useState(initialShowControls)
    const mediaRef = useRef(null)
    const controlsRef = useRef(null)
    const { currentTheme } = useTheme()
    const [isExpanded, setIsExpanded] = useState(false)

    const activeLessonRef = useRef(lessonId)

    const mediaSrc = videoSrc || audioSrc
    const mediaType = videoType || (videoSrc ? "video" : "audio")
    const isVideo = mediaType === "video" || mediaType === "mp4" || videoSrc

    const playerMode = getMediaPlayerMode(mediaSrc, srtSrc, mediaType)
    const controlsConfig = getControlsConfig(playerMode)

    useEffect(() => {
      setIsMuted(controlsConfig.defaultMuted)
    }, [controlsConfig.defaultMuted])

    useEffect(() => {
      if (!mediaSrc) return

      if (mediaRef.current) {
        mediaRef.current.load()
        setIsPlaying(false)
        onPlayStateChange?.(false)
      }
    }, [mediaSrc])

    useEffect(() => {
      if (activeLessonRef.current !== lessonId) {
        setIsPlaying(false)
        setIsMuted(controlsConfig.defaultMuted)
        setShowControls(initialShowControls)
        activeLessonRef.current = lessonId
      }
    }, [lessonId, initialShowControls, controlsConfig.defaultMuted])

    useImperativeHandle(ref, () => ({
      togglePlay: async () => {
        await togglePlay()
      },
      play: async () => {
        if (mediaSrc && mediaRef.current) {
          try {
            await mediaRef.current.play()
            setIsPlaying(true)
            onPlayStateChange?.(true)
          } catch (error) {
            console.warn("Auto-play failed, likely due to browser policies:", error)
            setIsPlaying(true)
            onPlayStateChange?.(true)
          }
        } else if (playerMode === "srt-only") {
          setIsPlaying(true)
          onPlayStateChange?.(true)
        }
      },
      pause: () => {
        if (mediaRef.current && isPlaying) {
          mediaRef.current.pause()
        }
        setIsPlaying(false)
        onPlayStateChange?.(false)
      },
      nextLine: () => {
        onNavigate?.("next")
      },
      prevLine: () => {
        onNavigate?.("prev")
      },
      getControlsVisible: () => {
        return showControls
      },
      setControlsVisible: (visible) => {
        setShowControls(visible)
      },
      toggleControlsVisible: () => {
        setShowControls(!showControls)
        return !showControls
      },
      getMuted: () => {
        return isMuted
      },
      setMuted: (muted) => {
        setIsMuted(muted)
        if (mediaRef.current) {
          mediaRef.current.muted = muted
        }
      },
    }))

    const togglePlay = async () => {
      if (mediaSrc && mediaRef.current) {
        try {
          if (isPlaying) {
            await mediaRef.current.pause()
            setIsPlaying(false)
          } else {
            await mediaRef.current.play()
            setIsPlaying(true)
          }
          onPlayStateChange?.(!isPlaying)
        } catch (error) {
          console.error("Play/Pause error:", error)
          setIsPlaying(!isPlaying)
          onPlayStateChange?.(!isPlaying)
        }
      } else if (playerMode === "srt-only") {
        setIsPlaying(!isPlaying)
        onPlayStateChange?.(!isPlaying)
      } else {
        setIsPlaying(!isPlaying)
        onPlayStateChange?.(!isPlaying)
      }
    }

    const handleRewind = () => {
      if (mediaRef.current) {
        mediaRef.current.currentTime = 0
        onTimeUpdate?.(0)
        if (isPlaying) {
          mediaRef.current.play().catch(console.error)
        }
      } else {
        onTimeUpdate?.(0)
        onNavigate?.("first")
      }
    }

    const toggleMute = () => {
      if (mediaRef.current) {
        mediaRef.current.muted = !isMuted
      }
      setIsMuted(!isMuted)
    }

    useEffect(() => {
      setShowControls(initialShowControls)
    }, [initialShowControls])

    useEffect(() => {
      const media = mediaRef.current
      if (!media) return

      const handleError = (e) => {
        console.error("Media error:", e)
        setIsPlaying(false)
        onPlayStateChange?.(false)
      }

      const handleTimeUpdateEvent = () => {
        onTimeUpdate?.(media.currentTime)
      }

      const handleEnded = () => {
        setIsPlaying(false)
        onPlayStateChange?.(false)
        if (hasNextLine) {
          setTimeout(() => onNavigate?.("next"), 500)
        }
      }

      media.addEventListener("error", handleError)
      media.addEventListener("timeupdate", handleTimeUpdateEvent)
      media.addEventListener("ended", handleEnded)

      return () => {
        media.removeEventListener("error", handleError)
        media.removeEventListener("timeupdate", handleTimeUpdateEvent)
        media.removeEventListener("ended", handleEnded)
      }
    }, [onTimeUpdate, onPlayStateChange, onNavigate, hasNextLine])

    const shouldRenderControls =
      controlsConfig.showPlayPause ||
      controlsConfig.showRewind ||
      controlsConfig.showVolume ||
      controlsConfig.showSubtitles ||
      controlsConfig.showNavigation

    if (!shouldRenderControls && playerMode === "text-only") {
      return null
    }

    return (
      <>
        <AnimatePresence>
          {showControls && (
            <motion.div
              ref={controlsRef}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="audio-controls-bar"
            >
              <motion.div
                className="bg-white rounded-full shadow-md flex items-center h-12"
                animate={{
                  width: isExpanded ? "auto" : "96px",
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <button
                  onClick={togglePlay}
                  className="flex items-center justify-center text-indigo-600 focus:outline-none w-12 h-12"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      className="flex items-center space-x-4 overflow-hidden"
                    >
                      {controlsConfig.showNavigation && (
                        <ChevronLeft
                          className={`w-5 h-5 ${hasPrevLine ? "text-indigo-600" : "text-gray-300"}`}
                          onClick={() => hasPrevLine && onNavigate("prev")}
                        />
                      )}

                      {controlsConfig.showRewind && (
                        <RotateCcw className="w-5 h-5 text-indigo-600" onClick={handleRewind} />
                      )}

                      {controlsConfig.showVolume && (
                        <button onClick={toggleMute}>
                          {isMuted ? (
                            <VolumeX className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <Volume2 className="w-5 h-5 text-indigo-600" />
                          )}
                        </button>
                      )}

                      {controlsConfig.showSubtitles && (
                        <Subtitles className="w-5 h-5 text-indigo-600" onClick={onSubtitlesToggle} />
                      )}

                      {controlsConfig.showNavigation && (
                        <ChevronRight
                          className={`w-5 h-5 ${hasNextLine ? "text-indigo-600" : "text-gray-300"}`}
                          onClick={() => hasNextLine && onNavigate("next")}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center justify-center text-indigo-600 focus:outline-none w-12 h-12"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isExpanded ? (
                      <motion.div
                        key="close"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <X className="w-5 h-5" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <Ellipsis className="w-5 h-5" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {mediaSrc && !isVideo && (
          <audio
            ref={mediaRef}
            src={mediaSrc}
            preload="auto"
            onError={(e) => console.error("Audio error:", e)}
            muted={isMuted}
          />
        )}
      </>
    )
  },
)

EnhancedAudioControls.displayName = "EnhancedAudioControls"

export default EnhancedAudioControls
